// Reglas compartidas de los destinos de transmisión (multidestino). Las usan el
// API, la tournament-app, la super-app y la pantalla del celular. No depende de
// Prisma: el tipo espeja el enum `StreamPlatform` del schema.

export type StreamPlatformName =
  | 'YOUTUBE'
  | 'TWITCH'
  | 'KICK'
  | 'FACEBOOK'
  | 'RESTREAM'
  | 'CUSTOM';

// Kick no tiene dirección por defecto: la da cada cuenta.
export const DESTINATION_DEFAULT_URLS: Partial<
  Record<StreamPlatformName, string>
> = {
  RESTREAM: 'rtmp://live.restream.io/live',
  FACEBOOK: 'rtmps://live-api-s.facebook.com:443/rtmp/',
  TWITCH: 'rtmp://live.twitch.tv/app',
};

export const DESTINATION_LABELS: Record<StreamPlatformName, string> = {
  YOUTUBE: 'YouTube',
  TWITCH: 'Twitch',
  KICK: 'Kick',
  FACEBOOK: 'Facebook',
  RESTREAM: 'Restream',
  CUSTOM: 'RTMP propio',
};

// Ayuda de cada destino con dirección y clave a mano (la comparten la
// tournament-app y la super-app). YouTube y Twitch no llevan: sus textos
// dependen de si hay canal vinculado.
export const DESTINATION_HELP: Partial<Record<StreamPlatformName, string>> = {
  FACEBOOK:
    'Usá la clave persistente de Facebook (la común es de un solo uso y se vence). Facebook exige RTMPS: la dirección ya viene cargada.',
  KICK: 'La dirección (RTMPS) y la clave las da Kick en el panel del streamer, en Configuración de transmisión.',
  RESTREAM:
    'Pegá la clave de Restream. Conectá YouTube, Twitch y el resto dentro de Restream: el celular hace una sola subida.',
};

// Espeja el enum `StreamDestinationStatus` del SDK.
export type StreamDestinationStatusName =
  | 'IDLE'
  | 'CONNECTING'
  | 'LIVE'
  | 'RECONNECTING'
  | 'ERROR';

export const DESTINATION_STATUS_LABELS: Record<
  StreamDestinationStatusName,
  string
> = {
  IDLE: 'En espera',
  CONNECTING: 'Conectando',
  LIVE: 'En vivo',
  RECONNECTING: 'Reconectando',
  ERROR: 'Error',
};

// Mensaje del destino Twitch por el canal vinculado cuya clave no se pudo
// leer al armar la config del celular: queda afuera de la subida y en ERROR
// con este texto, que el API también usa de marca (detener no lo borra).
export const TWITCH_RELINK_MESSAGE = 'Volvé a vincular Twitch';

export const IOS_SINGLE_DESTINATION =
  'En iPhone se transmite a un solo destino. Para llegar a varios lugares, usá Restream.';

// Android con un binario anterior a MultiCamera2 (le llegó el JS nuevo por
// OTA): sube sólo al primer destino, así que con 2 o más no se transmite.
export const ANDROID_MULTI_DESTINATION_UPDATE =
  'Actualizá la app para transmitir a varios destinos. Para llegar a varios lugares ahora, usá Restream.';

// «YouTube», «YouTube y Twitch», «YouTube, Twitch y Kick».
const joinNames = (names: string[]): string =>
  names.length < 2
    ? names.join('')
    : `${names.slice(0, -1).join(', ')} y ${names[names.length - 1]}`;

// 720p ≈ 3 Mbps por subida (el número que usa la spec).
const MBPS_PER_UPLOAD = 3;

interface DestinationLike {
  platform: StreamPlatformName;
}

// Cada destino es una subida completa desde el celular, incluidos los
// vinculados (YouTube/Twitch) y Restream (que es una sola hacia Restream).
export const uploadCount = (destinations: DestinationLike[]): number =>
  destinations.length;

export const multiDestinationWarning = (
  destinations: DestinationLike[]
): { title: string; lines: string[] } | null => {
  const uploads = uploadCount(destinations);
  if (uploads < 2) return null;

  const lines = [
    `Red: son ${uploads} subidas, cada destino es otra subida completa (~${uploads * MBPS_PER_UPLOAD} Mbps de subida a 720p, ~${MBPS_PER_UPLOAD} Mbps cada una).`,
    'Celular: gasta más batería y genera más calor; en una tarde larga puede bajar la calidad o cortarse.',
    'Recomendado: usá Restream, que manda a todos los destinos con una sola subida.',
  ];

  if (destinations.some((d) => d.platform === 'RESTREAM')) {
    const others = destinations.filter((d) => d.platform !== 'RESTREAM');
    const names = [
      ...new Set(others.map((d) => DESTINATION_LABELS[d.platform])),
    ];
    if (names.length > 0) {
      lines.push(
        `Ya tenés Restream: conectá ${joinNames(names)} dentro de Restream y dejalo como único destino.`
      );
    }
  }

  return { title: 'Transmitir a varios destinos exige más', lines };
};

export const duplicateDestinationError = (
  destinations: DestinationLike[]
): string | null => {
  const seen = new Set<StreamPlatformName>();
  for (const { platform } of destinations) {
    if (platform === 'CUSTOM') continue;
    if (seen.has(platform))
      return `${DESTINATION_LABELS[platform]} ya está agregado como destino.`;
    seen.add(platform);
  }
  return null;
};

// Plataformas habilitadas para agregar destinos nuevos. La lista vive en
// `AppConfig.streamPlatforms` (task `SetStreamPlatforms`); en null, valen
// éstas. Kick y Facebook arrancan apagadas.
export const DEFAULT_STREAM_PLATFORMS: StreamPlatformName[] = [
  'YOUTUBE',
  'TWITCH',
  'RESTREAM',
  'CUSTOM',
];

const PLATFORM_NAMES = Object.keys(DESTINATION_LABELS) as StreamPlatformName[];

/**
 * Lo guardado en `AppConfig.streamPlatforms` → la lista que vale. Cualquier
 * cosa que no sea una lista (null, JSON roto) cae en la de por defecto; los
 * nombres que no son plataformas se descartan.
 */
export const enabledStreamPlatforms = (
  stored: unknown
): StreamPlatformName[] =>
  Array.isArray(stored)
    ? PLATFORM_NAMES.filter((p) => stored.includes(p))
    : DEFAULT_STREAM_PLATFORMS;

export const disabledPlatformError = (platform: StreamPlatformName): string =>
  `Transmitir a ${DESTINATION_LABELS[platform]} todavía no está habilitado.`;
